import { useState } from 'react';
import MenuButton from 'components/header/menu-button';
import Dropdown from 'components/dropdown';
import styles from './style.module.scss';
import { buildClassName } from 'utils/build-class-name';
import TranslateIcon from '@mui/icons-material/Translate';
import { useIsMobile } from 'hooks/use-size-class';
import useOutsideClickHandler from 'hooks/use-outside-click-handler';

type LocaleOption = {
  id: string;
  label: string;
  isLink: boolean;
  onClick?: () => void;
};

type LocaleSwitcherProps = {
  shouldRender: boolean;
  currentLocale: string;
  localeOptions: LocaleOption[];
  changeLocale: (code: string) => Promise<void>;
  /** Compact treatment for the Area/Search V2 left rail (above the hamburger). */
  variant?: 'default' | 'rail';
};

export function LocaleSwitcher({
  shouldRender,
  currentLocale,
  localeOptions,
  changeLocale,
  variant = 'default',
}: LocaleSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const isMobile = useIsMobile();
  const isLocaleCode = /^[a-zA-Z]{2}-[a-zA-Z]{2}$/.test(currentLocale);
  const isRail = variant === 'rail';

  const onToggle = () => {
    setIsOpen(prev => !prev);
  };

  const onClose = () => {
    setIsOpen(false);
    setIsPinned(false);
  };

  const onRailTriggerClick = () => {
    setIsPinned(prev => {
      const nextPinned = !prev;
      setIsOpen(nextPinned);
      return nextPinned;
    });
  };

  const onRailPointerLeave = () => {
    if (!isPinned) {
      setIsOpen(false);
    }
  };

  // Rail uses a wrap+sibling popover (same pattern as RailMenu) so the menu is
  // not nested inside a <button> — nested interactive content was swallowing
  // clicks and preventing the options popover from opening.
  const railWrapRef = useOutsideClickHandler<HTMLDivElement>(isOpen ? onClose : undefined);

  if (!shouldRender) {
    return null;
  }

  const dropdownItems = localeOptions.map(option => ({
    ...option,
    onClick: async () => {
      if (option.onClick) {
        option.onClick();
      }

      try {
        await changeLocale(option.id);
        onClose();
      } catch (error) {
        console.error('Failed to change locale:', error);
      }
    },
  }));

  if (isRail) {
    return (
      <div
        className={styles.railWrap}
        ref={railWrapRef}
        data-testid="locale-switcher-wrap"
        onMouseLeave={onRailPointerLeave}
        onKeyDown={event => {
          if (event.key === 'Escape' && isOpen) {
            event.stopPropagation();
            onClose();
          }
        }}
      >
        <button
          type="button"
          className={buildClassName(styles.component, styles.rail)}
          onMouseEnter={() => setIsOpen(true)}
          onFocus={() => setIsOpen(true)}
          onClick={onRailTriggerClick}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          data-testid="locale-switcher"
        >
          <span className={styles.railLabel}>{currentLocale?.toUpperCase()}</span>
        </button>

        {isOpen && (
          <Dropdown
            items={dropdownItems}
            className={buildClassName(styles.dropdown, styles.railDropdown)}
          />
        )}
      </div>
    );
  }

  return (
    <MenuButton
      className={buildClassName(
        styles.component,
        isLocaleCode && !isMobile && styles.localeCode,
        isMobile && styles.iconOnly
      )}
      onClick={onToggle}
      closeMenu={onClose}
      testId="locale-switcher"
    >
      <div>
        <div id="locale-menu" className={styles.content}>
          {isMobile ? <TranslateIcon className={styles.icon} /> : currentLocale?.toUpperCase()}
        </div>

        {isOpen && (
          <Dropdown
            items={dropdownItems}
            className={styles.dropdown}
          />
        )}
      </div>
    </MenuButton>
  );
}
